import {
  Button,
  Card,
  Icon,
  IconButton,
  Image,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  Snackbar,
  Spacer,
  Stack,
  TextBody,
  TextSubtitle,
  TextCaption,
  TextTitle,
  Toggle,
  Tooltip,
  Input,
} from '@pypestream/design-system';
import { CSSProperties, FC, useEffect, useMemo, useRef, useState } from 'react';

import { SettingsEmptyState } from '../components/settings-empty-state';
import { SettingsSectionLoader } from '../components/settings-section-loader';
import { StudioPageProps, useLoadingState } from '../hooks';
import {
  buildDefaultMessagesSaves,
  describeConnection,
  EMPTY_MESSAGE,
  getChannelsFromPypes,
  getEnvDotStyle,
  getPlatformLogo,
  hasLiveAgentPlatform,
  isContactCenterPype,
  MessageState,
  PlatformMessages,
  resolvePlatformError,
  takesPlatformError,
  toExternalUrl,
  toPlatformMessages,
} from '../utils';
import { WithLoadingState } from '../router';
import { withStageNavRedirect } from './redirect-into-stage-nav';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { Pype } from '../xstate/prostudio.api-service.types';

type Env = 'live' | 'sandbox';
type ChannelMessageKey = 'busy' | 'offline';
export type MessageKey = keyof PlatformMessages;

const EMPTY_PLATFORM_MESSAGES: PlatformMessages = {
  busy: EMPTY_MESSAGE,
  offline: EMPTY_MESSAGE,
  technicalError: EMPTY_MESSAGE,
};

// Labels and order per Figma (Default Messages, 2026-10-02); the tooltips are
// the ticket's own descriptions of when each message is shown.
const CHANNEL_MESSAGE_FIELDS: {
  key: ChannelMessageKey;
  label: string;
  tooltip: string;
}[] = [
  {
    key: 'offline',
    label: "User can't be connected to a live agent",
    tooltip:
      "Shown when the user can't be connected to a live agent for any reason, not only when agents are offline.",
  },
  {
    key: 'busy',
    label: 'Waiting for an agent to accept',
    tooltip:
      'Shown after the handoff to the Live Agent Platform, before an agent accepts the chat.',
  },
];

// Off-screen but read by assistive tech. The DS has no visually-hidden helper.
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const PLATFORM_ERROR_TOOLTIP =
  'Shown when the automation platform hits an error, whatever Live Agent Platform the channel uses. Applies to every channel in this environment except Contact Center ones.';

const MessageField: FC<{
  label: string;
  tooltip: string;
  value: MessageState;
  disabled?: boolean;
  testId: string;
  onValueChange: (value: MessageState) => void;
}> = ({ label, tooltip, value, disabled, testId, onValueChange }) => (
  <>
    <Stack alignItems="center" justifyContent="space-between" nowrap>
      <Stack alignItems="center" gutter="2xsmall" nowrap>
        <TextBody size="small">{label}</TextBody>
        <Tooltip test-id={`${testId}-info`} placement="right">
          {/* Focusable so the tooltip (which opens on focus too) is reachable
              without a mouse — it is the only description of the field. */}
          <Icon
            name="info"
            color="gray"
            size="small"
            tabIndex={0}
            role="img"
            aria-label={`About ${label}`}
          />
          <TextCaption variant="primary" slot="content">
            {tooltip}
          </TextCaption>
        </Tooltip>
      </Stack>
      <Toggle
        test-id={`${testId}-enable`}
        checked={value.enable}
        disabled={disabled}
        onChange={(e) => onValueChange({ ...value, enable: e.target.checked })}
      >
        {/* The toggle's accessible name: ps-toggle labels its native checkbox
            with slotted text, and the visible label sits outside it. */}
        <span style={VISUALLY_HIDDEN}>{`${label} enabled`}</span>
      </Toggle>
    </Stack>
    <Spacer size="2xsmall" />
    <Input
      test-id={testId}
      variant="outlined"
      value={value.text}
      disabled={disabled}
      onChange={(e) =>
        onValueChange({ ...value, text: (e.target as HTMLInputElement).value })
      }
      onInput={(e) =>
        onValueChange({ ...value, text: (e.target as HTMLInputElement).value })
      }
    />
  </>
);

export const PlatformSection: FC<{
  pype: Pype;
  messages: PlatformMessages;
  onMessageChange: (key: ChannelMessageKey, value: MessageState) => void;
}> = ({ pype, messages, onMessageChange }) => {
  const [expanded, setExpanded] = useState(true);
  const managedExternally = isContactCenterPype(pype);

  // Name the card after the channel, not its pype: the pype name is a derived
  // `{bot}-{channel}` slug ("Pypestreampmremlsernjrzfhatguooche-test123") that
  // appears nowhere else in the app. Fall back to it only when no channel can be
  // derived (a pype with no widget/voice/Contact Center stream), so the card is
  // never unlabelled.
  const channel = useMemo(() => getChannelsFromPypes([pype])[0], [pype]);
  const title = channel?.name || pype.name;
  // Greyed-out identity of the connection behind it, per Figma
  // ("Account ID: 142 Inbox ID 1111", "hbomax.my.salesforce.com").
  const connection = describeConnection(pype.agent_routing_config);
  const contactCenterUrl = managedExternally
    ? toExternalUrl(pype.agent_routing_url)
    : undefined;

  return (
    <Card
      size="medium"
      border="default"
      shadow="none"
      test-id={`ps-default-messages-platform-${pype.id}`}
    >
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <Image
            src={getPlatformLogo(pype.agent_routing)}
            width="24px"
            height="24px"
          />
          <TextSubtitle size="small">{title}</TextSubtitle>
          {connection && (
            <TextBody size="small" variant="tertiary">
              {connection}
            </TextBody>
          )}
        </Stack>
        <IconButton
          test-id={`ps-default-messages-platform-${pype.id}-toggle`}
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
        />
      </Stack>

      {expanded && (
        <>
          {managedExternally && (
            <>
              <Spacer size="medium" />
              {/* Notice box per Figma: purple-300 @ 10% fill, purple-300
                  border, purple-500 info icon. */}
              <div
                style={{
                  background: 'rgba(133, 115, 231, 0.1)',
                  border: '1px solid var(--purple-300, #8573e7)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
              >
                <Stack alignItems="center" gutter="xsmall" nowrap>
                  <Icon
                    name="info"
                    size="small"
                    style={{
                      ['--icon-color' as string]: 'var(--purple-500, #5956e3)',
                    }}
                  />
                  <TextBody size="small">
                    Auto-response copy is managed at the inbox level in Contact
                    Center and is not configurable here.
                  </TextBody>
                  {contactCenterUrl && (
                    <a
                      data-testid={`ps-default-messages-platform-${pype.id}-cc-link`}
                      href={contactCenterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        // purple-500, not the Figma purple-300: 12px
                        // purple-300 on this fill is ~3.6:1, under WCAG 1.4.3.
                        color: 'var(--purple-500, #5956e3)',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Manage copy in Contact Center
                      <span style={VISUALLY_HIDDEN}> (opens in a new tab)</span>
                    </a>
                  )}
                </Stack>
              </div>
            </>
          )}

          {CHANNEL_MESSAGE_FIELDS.map(({ key, label, tooltip }) => (
            <div key={key}>
              <Spacer size="medium" />
              <MessageField
                label={label}
                tooltip={tooltip}
                value={messages[key]}
                disabled={managedExternally}
                testId={`ps-default-messages-platform-${pype.id}-${key}`}
                onValueChange={(value) => onMessageChange(key, value)}
              />
            </div>
          ))}
        </>
      )}
    </Card>
  );
};

const DefaultMessagesPage: FC<StudioPageProps> = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  const [language, setLanguage] = useState('en');
  const [draft, setDraft] = useState<Record<string, PlatformMessages>>({});
  const [touchedIds, setTouchedIds] = useState<Set<string>>(new Set());
  // `undefined` until the Platform error field is edited: only an edit writes
  // it to every channel, so merely opening the page never rewrites anything.
  const [platformErrorDraft, setPlatformErrorDraft] = useState<
    MessageState | undefined
  >();

  const defaultMessagesPypes = useProStudioCtxSelector(
    (ctx) => ctx.defaultMessagesPypes
  );

  // Load once. See live-agent-platforms.tsx for why this is gated on
  // useLoadingState() too, not just hasLoaded.
  const isPageLoading = useLoadingState();
  const hasLoaded = defaultMessagesPypes != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadDefaultMessages' });
    }
  }, [isPageLoading, hasLoaded]);

  const channels = useMemo(
    () => defaultMessagesPypes?.[env] ?? [],
    [defaultMessagesPypes, env]
  );
  // Busy/offline belong to a Live Agent Platform, so only a channel connected
  // to one gets a card. A channel never connected, or bound to AgentPro (no
  // longer supported), only takes the Platform error above.
  const platforms = useMemo(
    () => channels.filter((pype) => hasLiveAgentPlatform(pype.agent_routing)),
    [channels]
  );
  const platformError = useMemo(
    () => resolvePlatformError(channels),
    [channels]
  );
  const hasPlatformErrorTargets = channels.some(takesPlatformError);

  // The env a save chain is running for, or null. See the reset effect below.
  const savingEnvRef = useRef<Env | null>(null);
  const [completedSaves, setCompletedSaves] = useState(0);

  const resetDraft = () => {
    setDraft(
      Object.fromEntries(
        channels.map((pype) => [pype.id, toPlatformMessages(pype)])
      )
    );
    setTouchedIds(new Set());
    setPlatformErrorDraft(undefined);
    savingEnvRef.current = null;
  };

  // Reset the draft to the loaded values whenever the source data or env
  // changes, discarding any in-progress edits for the previous env.
  //
  // Except mid-chain: every save in the chain replaces `defaultMessagesPypes`,
  // so resetting there would close the snackbar after the first channel and,
  // if a later save failed, throw away the edits it never sent. The reset waits
  // for the chain's final callback (`completedSaves`); a failed chain keeps the
  // edits so Save can be retried, and Cancel or an env switch still resets.
  // `channels` is memoised on exactly `defaultMessagesPypes` and `env`.
  useEffect(() => {
    if (savingEnvRef.current === env) return;
    resetDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, completedSaves]);

  const handleMessageChange = (
    pypeId: string,
    key: ChannelMessageKey,
    value: MessageState
  ) => {
    setDraft((prev) => ({
      ...prev,
      [pypeId]: { ...(prev[pypeId] ?? EMPTY_PLATFORM_MESSAGES), [key]: value },
    }));
    setTouchedIds((prev) => new Set(prev).add(pypeId));
  };

  const handleSave = () => {
    // The machine handles `updateDefaultMessages` only in `idle`, and the first
    // event transitions it out of `idle` — so dispatching one per channel in a
    // synchronous loop had xstate silently drop every save after the first:
    // only one channel persisted, the rest stayed dirty, and the success toast
    // fired anyway. Chain each save off the previous one's callback, the same
    // way `channel-settings-form` serializes its own idle-only flows.
    //
    // A failed save never invokes its callback, so the chain stops there rather
    // than reporting the remaining channels as saved.
    const saves = buildDefaultMessagesSaves({
      pypes: channels,
      drafts: draft,
      touchedIds,
      platformError: platformErrorDraft,
    }).map(({ pypeId, autoresponders }) => (next?: () => void) => {
      sendProStudioEvent({
        type: 'updateDefaultMessages',
        payload: {
          id: pypeId,
          env,
          autoresponders,
          cb: () => {
            setTouchedIds((prev) => {
              const remaining = new Set(prev);
              remaining.delete(pypeId);
              return remaining;
            });
            next?.();
          },
        },
      });
    });

    savingEnvRef.current = env;
    saves.reduceRight<(() => void) | undefined>(
      (next, dispatch) => () => dispatch(next),
      () => {
        savingEnvRef.current = null;
        setCompletedSaves((count) => count + 1);
      }
    )?.();
  };

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle test-id="ps-default-messages-page-title" size="small">
          Default Messages
        </TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-default-messages-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          <Select
            test-id="ps-default-messages-language"
            variant="outlined"
            value={language}
            onChange={(e) => setLanguage(e.target.value as string)}
          >
            <SelectOption value="en">English - American (en)</SelectOption>
          </Select>
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      {/* The loader / empty state / content swap stays inside a wrapper React
          fully owns. The Snackbar below relocates itself into a portal while
          open and only moves back after its closing transition, so a branch
          swapped as its direct sibling is inserted before a node no longer in
          the page — an env switch with unsaved edits closes the snackbar and
          swaps the branch in one commit, and React throws `insertBefore`.
          `portaled-modal-sibling.test.tsx` pins the shape. */}
      <div>
        {!hasLoaded ? (
          <PageSection size="small">
            <SettingsSectionLoader testId="ps-default-messages-loading" />
          </PageSection>
        ) : channels.length === 0 ? (
          <PageSection size="small">
            {/* Unlike the Project Settings lists, an empty list here doesn't mean
              "you haven't created one" — Default Messages are per channel, so
              the fix is to add a channel to this solution, not anything on
              this screen. */}
            <SettingsEmptyState
              testId="ps-default-messages-empty"
              title="No channels in this environment"
              description={`Add a ${env === 'live' ? 'Live' : 'Sandbox'} channel to this solution to set its default messages.`}
            />
          </PageSection>
        ) : (
          <Stack direction="column" gutter="large" alignItems="stretch">
            <PageSection
              size="small"
              test-id="ps-default-messages-conversation-section"
            >
              <TextTitle size="xsmall">Conversation Handling</TextTitle>
              <Spacer size="medium" />
              {/* One field for the whole environment, though the backend stores
                it per channel: saving writes it to every channel except
                Contact Center ones. The toggle is a deliberate deviation from
                the Figma, agreed with product on the ticket — without it,
                saving would silently switch the message on everywhere. */}
              <MessageField
                label="Platform error"
                tooltip={PLATFORM_ERROR_TOOLTIP}
                value={platformErrorDraft ?? platformError.value}
                disabled={!hasPlatformErrorTargets}
                testId="ps-default-messages-platform-error"
                onValueChange={setPlatformErrorDraft}
              />
              {/* A live region that is always mounted, so the note is announced
                when it appears and not only read in passing. */}
              <div role="status">
                {platformError.mixed && (
                  <>
                    <Spacer size="2xsmall" />
                    <TextCaption
                      test-id="ps-default-messages-platform-error-mixed"
                      variant="secondary"
                    >
                      Channels in this environment currently use different
                      Platform error messages. Saving applies this one to all of
                      them.
                    </TextCaption>
                  </>
                )}
              </div>
            </PageSection>

            <PageSection
              size="small"
              test-id="ps-default-messages-platforms-section"
            >
              <TextTitle size="xsmall">Live Agent Platforms</TextTitle>
              <Spacer size="medium" />
              {platforms.length === 0 ? (
                <SettingsEmptyState
                  testId="ps-default-messages-platforms-empty"
                  title="No channels connected to a Live Agent Platform"
                  description="Busy and offline messages apply only to channels connected to a Live Agent Platform."
                />
              ) : (
                <Stack direction="column" gutter="medium" alignItems="stretch">
                  {platforms.map((pype) => (
                    <PlatformSection
                      key={pype.id}
                      pype={pype}
                      messages={
                        draft[pype.id ?? ''] ?? toPlatformMessages(pype)
                      }
                      onMessageChange={(key, value) =>
                        pype.id && handleMessageChange(pype.id, key, value)
                      }
                    />
                  ))}
                </Stack>
              )}
            </PageSection>
          </Stack>
        )}
      </div>

      <Snackbar
        test-id="ps-default-messages-snackbar"
        open={touchedIds.size > 0 || platformErrorDraft !== undefined}
      >
        There are unsaved changes
        <Button
          test-id="ps-default-messages-cancel"
          variant="secondary"
          size="large"
          onClick={resetDraft}
          slot="action"
        >
          Cancel
        </Button>
        <Button
          test-id="ps-default-messages-save"
          size="large"
          onClick={handleSave}
          slot="action"
        >
          Save Changes
        </Button>
      </Snackbar>
    </>
  );
};

export const DefaultMessages = withStageNavRedirect(
  WithLoadingState(DefaultMessagesPage),
  { stage: 'design' }
);
